// Imperative paint pass for pedigree data overlays (TGCLOUD-1670).
//
// Marks are children of each matched node's own <g>, which buys three things
// with no code: they follow a live drag (the drag handler rewrites only the
// group transform), they scale with zoom (.diagram is the only transformed
// group), and they cannot affect layout (computeLabelLayout reserves space for
// label rows and nothing else).
//
// The ring is inserted as the group's FIRST child so it paints BEHIND the
// symbol, the disease pie, the carrier perimeter and every corner tenant: the
// cascade badge, the linked-record badge, the proxy P, the nomenclature number,
// the pinned dot and the records-reviewed asterisk all win over it by document
// order. That is the whole reason the ring can be as large as it is.
//
// Every element carries data-export="exclude" so one selector removes the lot
// from the exported SVG. Nothing here is a React render: Canvas rebuilds its SVG
// from scratch on every render pass, so this is re-applied at the end of that
// pass and again from a cheap version-keyed effect.

import { markGeometry } from './geometry';

const NS = 'http://www.w3.org/2000/svg';

export const MARK_CLASS = 'data-overlay-mark';
export const DIM_ATTR = 'data-overlay-dim';
export const EXPORT_EXCLUDE_SELECTOR = '[data-export="exclude"]';

const svgEl = (name, attrs) => {
  const el = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) {
    if (v != null) el.setAttribute(k, String(v));
  }
  return el;
};

/** Remove every mark and undo every dim. Idempotent, so paint can call it first. */
export function clearDataOverlays(root) {
  if (!root) return;
  root.querySelectorAll(`.${MARK_CLASS}`).forEach((el) => el.remove());
  root.querySelectorAll(`[${DIM_ATTR}]`).forEach((el) => {
    el.removeAttribute('opacity');
    el.removeAttribute(DIM_ATTR);
  });
}

/** Is this node drawn as a circle? Read from the symbol path, as the hover ring does. */
function isRoundNode(group) {
  try {
    const te = group.querySelector('path.te');
    return /[aA]/.test(te?.getAttribute('d') || '');
  } catch (_) {
    return false;
  }
}

function paintOne(group, { slot, colour, text, band, title, symbolSize }) {
  const isRound = isRoundNode(group);
  const geo = markGeometry(symbolSize, { isRound, slot, text, band });

  const ring = svgEl('circle', {
    class: `${MARK_CLASS} data-overlay-ring`,
    'data-export': 'exclude',
    'data-overlay-slot': slot,
    cx: 0,
    cy: 0,
    r: geo.ring.radius,
    fill: 'none',
    stroke: colour,
    'stroke-width': geo.ring.strokeWidth,
    'stroke-dasharray': geo.ring.dashArray,
    'pointer-events': 'none'
  });
  // insertBefore, not append: the ring must paint behind everything the node
  // already draws. Appending would put it over the labels and the corner marks.
  group.insertBefore(ring, group.firstChild);

  if (!text) return;

  const tag = svgEl('g', {
    class: `${MARK_CLASS} data-overlay-tag`,
    'data-export': 'exclude',
    'data-overlay-slot': slot,
    // The tag is the only hit target the overlay adds. Node clicks are bound on
    // path.te, so this steals no selection click, and drag is bound on the group,
    // so dragging from the tag still works.
    'pointer-events': 'all'
  });
  tag.appendChild(
    svgEl('rect', {
      x: geo.tag.x,
      y: geo.tag.y,
      width: geo.tag.width,
      height: geo.tag.height,
      rx: geo.tag.rx,
      fill: colour,
      stroke: '#fff',
      'stroke-width': geo.tag.strokeWidth
    })
  );
  const label = svgEl('text', {
    x: geo.tag.textX,
    y: geo.tag.textY,
    'text-anchor': 'middle',
    'dominant-baseline': 'central',
    'font-size': geo.tag.fontSize,
    'font-weight': 'bold',
    fill: '#fff'
  });
  label.textContent = text;
  tag.appendChild(label);

  if (title) {
    const t = document.createElementNS(NS, 'title');
    t.textContent = title;
    tag.appendChild(t);
  }
  group.appendChild(tag);
}

/**
 * Draw the active overlays.
 *
 * @param {Element} root      the chart's container, NEVER document: pids are
 *                            reused across families and several charts can be
 *                            mounted at once in the workspace
 * @param {Array}   slots     [{ results, colour }] in slot order, at most two
 * @param {object}  style     { treatment: 'ring' | 'dim' }
 * @param {number}  symbolSize config.SYMBOL_SIZE for this render
 */
export function paintDataOverlays(
  root,
  slots = [],
  style = {},
  symbolSize = 155
) {
  if (!root) return;
  clearDataOverlays(root);
  const active = slots.filter(
    (s) => s && s.results && s.results.matches.length >= 0
  );
  if (!active.length) return;

  const matchedPids = new Set();

  active.forEach((slotState, slot) => {
    const { results, colour } = slotState;
    for (const match of results.matches) {
      for (const pid of match.pids) {
        matchedPids.add(pid);
        const group = root.querySelector(`g[data-selection-pid="${pid}"]`);
        if (!group) continue;
        paintOne(group, {
          slot,
          colour,
          text: match.detail?.pill || '',
          band: match.detail?.weight || null,
          title: match.detail?.summary || '',
          symbolSize
        });
      }
    }
  });

  if (style.treatment !== 'dim') return;

  // Dimming by group opacity rather than a veil rectangle: it covers the symbol,
  // the corner marks and the label stack exactly, has no effect on the diagram
  // bounding box, follows a drag, works in both themes (a white veil would
  // BRIGHTEN non-matches on a dark background), and is undone by removing two
  // attributes. Verified that nothing else writes opacity on a node group.
  root.querySelectorAll('g[data-selection-pid]').forEach((group) => {
    const pid = group.getAttribute('data-selection-pid');
    if (matchedPids.has(Number(pid)) || matchedPids.has(pid)) return;
    group.setAttribute('opacity', '0.25');
    group.setAttribute(DIM_ATTR, '1');
  });
  const edges = root.querySelector('g.edges');
  if (edges) {
    edges.setAttribute('opacity', '0.4');
    edges.setAttribute(DIM_ATTR, '1');
  }
}
